import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { UnsavedChangesDialog } from './shared/UnsavedChangesAlert';
import { DraggableDialog } from './DraggableDialog';
import { DialogProvider, useDialog } from '@/providers/DialogProvider';
import { useMapStore } from '@/stores/mapStore';
import { useDialogManager } from '@/hooks/useDialogManager';
import { useAuth } from '@/providers/AuthProvider';
import { UserRole } from '@packages/database/types';
import { CreateTicketForm } from '@/features/ticket/components/CreateTicketDialog/CreateTicketForm';
import { EditTicketForm } from '@/features/ticket/components/TicketDetailsDialog/EditTicketForm';
import { CreateProjectForm } from '@/features/project/components/CreateProjectForm';
import { EditProjectForm } from '@/features/project/components/EditProjectForm';
import { CreateAttributeForm } from '@/features/admin/settings/project/CreateAttributeForm';
import { CreateProjectTypeForm } from '@/features/admin/settings/project/CreateProjectTypeForm';
import { CreateSkillForm } from '@/features/admin/settings/skills/CreateSkillForm';
import { CreateCrewForm } from '@/features/admin/crew-management/components/forms/CreateCrewForm';
import { EditCrewForm } from '@/features/admin/crew-management/components/forms/EditCrewForm';

// =========================================================================
// CONFIG
// =========================================================================

interface DialogConfig {
  Component: React.ComponentType<any>;
  title: string;
  maxWidth?: string;
  draggable?: boolean;
  requiresId?: boolean;
  permissions?: UserRole[];
}

const DIALOG_CONFIG: Record<string, DialogConfig> = {
  'create-ticket': {
    Component: CreateTicketForm,
    title: 'Create New Ticket',
    draggable: true,
    maxWidth: 'max-w-4xl',
    permissions: ['ADMIN'],
  },
  'edit-ticket': {
    Component: EditTicketForm,
    title: 'Edit Ticket',
    draggable: true,
    maxWidth: 'sm:max-w-2xl',
    requiresId: true,
  },
  'create-project': {
    Component: CreateProjectForm,
    title: 'Create New Project',
    maxWidth: 'sm:max-w-2xl',
    permissions: ['ADMIN'],
  },
  'edit-project': {
    Component: EditProjectForm,
    title: 'Edit Project',
    maxWidth: 'max-w-4xl',
    requiresId: true,
    permissions: ['ADMIN'],
  },
  'create-custom-attribute': {
    Component: CreateAttributeForm,
    title: 'Create Custom Attribute',
    maxWidth: 'max-w-xl',
    permissions: ['ADMIN'],
  },
  'create-project-type': {
    Component: CreateProjectTypeForm,
    title: 'Create Project Type',
    maxWidth: 'max-w-2xl',
    permissions: ['ADMIN'],
  },
  'create-skill': {
    Component: CreateSkillForm,
    title: 'Create Skill',
    maxWidth: 'max-w-xl',
    permissions: ['ADMIN'],
  },
  'edit-skill': {
    Component: CreateSkillForm,
    title: 'Edit Skill',
    maxWidth: 'max-w-xl',
    requiresId: true,
    permissions: ['ADMIN'],
  },
  'create-crew': {
    Component: CreateCrewForm,
    title: 'Create Crew',
    maxWidth: 'max-w-2xl',
    permissions: ['ADMIN'],
  },
  'edit-crew': {
    Component: EditCrewForm,
    title: 'Edit Crew',
    maxWidth: 'max-w-2xl',
    requiresId: true,
    permissions: ['ADMIN'],
  },
};

// =========================================================================
// PERMISSION HOOK
// =========================================================================

const useUserRole = (): UserRole | null => {
  const { user, authLoading } = useAuth();
  if (authLoading || !user) return null;
  return user.role as UserRole;
};

// =========================================================================
// DIALOG CONTROLLER
// =========================================================================

const DialogController = () => {
  const { authLoading } = useAuth();
  const { dialogType, entityId, closeDialog } = useDialogManager();

  const { isDirty, setIsDirty } = useDialog();
  const [isConfirmingClose, setConfirming] = useState(false);
  const userRole = useUserRole();

  const setInteractionMode = useMapStore((s) => s.setInteractionMode);

  // Reset dirty state when switching between dialogs so stale state
  // from the previous form doesn't block the new one
  useEffect(() => {
    setIsDirty(false);
    setConfirming(false);
  }, [dialogType, entityId, setIsDirty]);

  const config = dialogType ? DIALOG_CONFIG[dialogType] : null;

  const hasPermission =
    !config?.permissions?.length ||
    (!!userRole && config.permissions.includes(userRole));

  const canRender =
    !!config && hasPermission && (!config.requiresId || !!entityId);

  // ── Stable panelId derived from URL state ─────────────────────────────
  const panelId = useMemo(
    () =>
      dialogType ? `${dialogType}${entityId ? `-${entityId}` : ''}` : 'unknown',
    [dialogType, entityId],
  );

  const handleFullClose = useCallback(() => {
    closeDialog();
    setIsDirty(false);
    setConfirming(false);

    if (dialogType?.includes('ticket')) {
      setInteractionMode('view');
    }
  }, [closeDialog, dialogType, setInteractionMode, setIsDirty]);

  const handleAttemptClose = useCallback(() => {
    if (isDirty) {
      setConfirming(true);
    } else {
      handleFullClose();
    }
  }, [isDirty, handleFullClose]);

  // Global ESC key listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dialogType && !isConfirmingClose) {
        e.preventDefault();
        handleAttemptClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dialogType, isConfirmingClose, handleAttemptClose]);

  if (authLoading || !config) return null;

  if (!canRender) return null;

  const formProps = {
    id: entityId,
    onSuccess: handleFullClose,
    onClose: handleAttemptClose,
    key: entityId,
  };

  return (
    <>
      {config.draggable ? (
        <DraggableDialog
          isOpen={!!dialogType}
          onClose={handleAttemptClose}
          title={config.title}
          width={700}
          panelId={panelId}
        >
          <config.Component {...formProps} />
        </DraggableDialog>
      ) : (
        <Dialog
          open={!!dialogType}
          onOpenChange={(open) => !open && handleAttemptClose()}
        >
          <DialogContent
            onInteractOutside={(e) => {
              if (isDirty) e.preventDefault();
            }}
            className={cn(
              'flex flex-col p-0 gap-0 bg-card',
              'w-[95vw] sm:w-full h-[95vh] sm:h-auto sm:max-h-[90vh]',
              config.maxWidth ?? 'max-w-2xl',
            )}
          >
            <DialogHeader className="flex-shrink-0 px-6 py-4 border-b">
              <DialogTitle className="text-xl font-semibold">
                {config.title}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-6">
              <config.Component {...formProps} />
            </div>
          </DialogContent>
        </Dialog>
      )}

      <UnsavedChangesDialog
        open={isConfirmingClose}
        onConfirm={handleFullClose}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};

// =========================================================================
// ROOT
// =========================================================================

export const DialogManager = () => (
  <DialogProvider>
    <DialogController />
  </DialogProvider>
);
